import React, { useContext, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { node, bool, object } from 'prop-types'
import ReactTooltip from 'react-tooltip'
import { navigate } from 'gatsby'
import { useLocation } from '@reach/router'

import useQueryParams from 'hooks/useQueryParams'
import { initImpact, initGlassroom, getSessionID } from 'utils/tracking'
import NativeAuthContext from 'components/NativeAuth'
import { navHeightMobile, navHeight } from 'styles/global'
import { white } from 'styles/colors'
import Header from 'components/Header'
import HealthWarning from 'components/HealthWarning'
import Footer from 'components/Footer'
import { media } from 'styles/media'
import SignUpModal from 'components/Modal/SignUpModal'
import LoginModal from 'components/Modal/LogInModal'
import MissingAddressModal from 'components/Modal/MissingAddressModal'
import VerifyEmailModal from 'components/Modal/VerifyEmailModal'
import PriceIncreaseBanner from 'components/home/PriceIncreaseBanner'
import CancelSubscriptionModal from 'components/Modal/CancelSubscriptionModal'
import PauseSubscriptionModal from 'components/Modal/PauseSubscriptionModal'
import SubscriptionModal from 'components/Modal/SubscriptionModal'
import Hotjar from 'components/Hotjar'
import MetaTags from 'components/MetaTags'
import VypePlusDiscountCodesModal from 'components/Modal/VypePlusDiscountCodesModal'
import Portal from 'components/Portal'
import 'react-dates/initialize'
import SEO_DATA from 'utils/seoData'
import useDigitalData from 'hooks/useDigitalData'

const Outer = styled.div`
  position: relative;
`

const Inner = styled.div`
  width: 100%;
  background-color: ${white};
  min-height: ${props => (props.fullHeight ? '100vh' : '0')};
  display: flex;
  flex-direction: column;

  ${props =>
    props.hasPaddingTop &&
    css`
      padding-top: ${navHeightMobile};

      ${media.medium`
        padding-top: ${navHeight};
      `};
    `}
`

const Layout = ({ children, hideHeader, hideFooter, fullHeight, stickyHeader, hideWarning, lightFooter }) => {
  const {
    user,
    signUpModalVisibility,
    setSignupModalVisibility,
    loginModalVisibility,
    setLoginModalVisibility,
    missingAddressModalVisibility,
    setMissingAddressModalVisibility,
    verifyEmailModalVisibility,
    setVerifyEmailModalVisibility,
    loginError,
    login,
    sendVypeVerificationEmail,
    cancelSubscriptionModalVisibility,
    setCancelSubscriptionModalVisibility,
    pauseSubscriptionModalVisibility,
    setPauseSubscriptionModalVisibility,
    vypePlusDiscountCodesModalVisibility,
    setVypePlusDiscountCodesModalVisibility,
    setNativeSoftVerifiedUser
  } = useContext(NativeAuthContext)

  const queryParams = useQueryParams()
  const location = useLocation()
  const { digitalData } = useDigitalData()

  const hasPaddingTop = !stickyHeader && !hideHeader

  const trackPage = () => {
    digitalData.push({
      page: {
        pageUrl: location.pathname,
        pageFullUrl: location.hostname + location.pathname + location.search,
        pageTitle: document.title,
        pageName: '',
        pageType: '',
        pageHierarchy: location.pathname
          .split('/')
          .filter(s => s.length)
          .reduce((acc, s, i) => ({ ...acc, [`level${i + 1}`]: s }), {}),
        timestamp: new Date().getTime()
      },
      session: {
        timestamp: new Date().getTime(),
        sessionId: getSessionID(),
        loginInfo: {
          loginStatus: user ? 'logged-in' : 'logged-out',
          loginSource: localStorage.urlAtLogin
        },
        postalCode: user ? user.postalCode?.substr(0, 3) || '' : '',
        stateProvince: user ? user.province : ''
      }
    })
  }

  useEffect(() => {
    const observer = new MutationObserver(() => {
      if (!document.title) return
      trackPage()
    }).observe(document.querySelector('title'), { subtree: true, characterData: true })

    return () => {
      observer?.disconnect()
    }
  }, [])

  useEffect(() => {
    if (!document.title) return
    trackPage()
  }, [location.pathname, location.hostname, location.search])

  useEffect(() => {
    if (process.browser && localStorage.province === 'QC') {
      navigate('/qc', { replace: true })
    }
  }, [])

  useEffect(() => {
    if (queryParams.province) {
      setNativeSoftVerifiedUser({ province: queryParams.province }, false)
      if (queryParams.province === 'ON' && window.location.pathname === (process.env.BASE_PATH || '') + '/') {
        navigate('/ontario/')
      }
    }

    if (queryParams.province) {
      setNativeSoftVerifiedUser({ province: queryParams.province }, false)
      if (queryParams.province === 'BC' && window.location.pathname === (process.env.BASE_PATH || '') + '/') {
        navigate('/bc/')
      }
    }

    if (queryParams.showLogin || queryParams.showRegister) {
      setTimeout(() => {
        setSignupModalVisibility(true)
      }, 2000)
    }
  }, [queryParams])

  useEffect(() => {
    initGlassroom(window, document, 'script', 'dataLayer', 'GTM-PMMMQKT')
    initImpact('//d.impactradius-event.com/A2068476-6317-453c-b0e7-b54625f5a8a81.js', 'script', 'ire', document, window)
  }, [])

  const utmCampaign = queryParams.utm_campaign || ''
  const utmSource = queryParams.utm_source || ''
  const utmMedium = queryParams.utm_medium || ''

  let metaTags
  if (typeof window !== `undefined`) {
    metaTags = SEO_DATA.find(page => window.location.pathname === (process.env.BASE_PATH || '') + page.url)
  }

  return (
    <>
      {metaTags && <MetaTags title={metaTags.title} description={metaTags.description} />}

      {!hideWarning && <HealthWarning />}

      <PriceIncreaseBanner />

      <Outer>
        {!hideHeader && <Header isSticky={stickyHeader} />}
        <Inner hideHeader={hideHeader} fullHeight={fullHeight} hasPaddingTop={hasPaddingTop}>
          {children}
        </Inner>
        {!hideFooter && <Footer isLight={lightFooter} />}
      </Outer>
      <Portal selector="misc">
        <ReactTooltip effect="solid" className="tooltip" />
      </Portal>
      {!user && (
        <SignUpModal
          visible={signUpModalVisibility}
          close={() => {
            setSignupModalVisibility(false)
          }}
          utmCampaign={utmCampaign}
          utmSource={utmSource}
          utmMedium={utmMedium}
          queryParams={queryParams}
        />
      )}
      <LoginModal
        visible={loginModalVisibility}
        close={() => {
          setLoginModalVisibility(false)
        }}
        loginError={loginError}
        handleUserLogin={login}
        setSignupModalVisibility={setSignupModalVisibility}
      />
      {user && (
        <>
          <VerifyEmailModal
            visible={verifyEmailModalVisibility}
            sendVypeVerificationEmail={() => {
              sendVypeVerificationEmail({ ...user })
            }}
            close={() => {
              setVerifyEmailModalVisibility(false)
            }}
          />
          <MissingAddressModal
            visible={missingAddressModalVisibility}
            close={() => {
              setMissingAddressModalVisibility(false)
            }}
            setMissingAddressModalVisibility={setMissingAddressModalVisibility}
          />
          <VypePlusDiscountCodesModal
            visible={vypePlusDiscountCodesModalVisibility}
            close={() => {
              setVypePlusDiscountCodesModalVisibility(false)
            }}
            vypePlusDiscountCodes={user.vypePlusDiscountCodes}
          />
        </>
      )}
      <CancelSubscriptionModal
        visible={cancelSubscriptionModalVisibility}
        close={() => {
          setCancelSubscriptionModalVisibility(false)
        }}
      />
      <PauseSubscriptionModal
        visible={pauseSubscriptionModalVisibility}
        close={() => {
          setPauseSubscriptionModalVisibility(false)
        }}
      />
      <SubscriptionModal />
      <Hotjar />
    </>
  )
}

Layout.propTypes = {
  children: node.isRequired,
  hideHeader: bool,
  fullHeight: bool,
  stickyHeader: bool,
  hideFooter: bool,
  hideWarning: bool,
  lightFooter: bool,
  router: object
}

export default Layout
